// apps/web/src/services/api.js

import axios from 'axios';

/**
 * API Configuration
 *
 * - In production: Uses https://tasks.sentia-app.com/api/v1
 * - In development: Uses http://localhost:3001/api/v1
 *
 * - "Sentia login" is simulated by our own API in dev at:
 *     POST http://localhost:3001/api/v1/dev/auth/login
 *
 * - The main Tasks API lives at:
 *     /api/v1
 */

const TASKS_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
const SENTIA_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '/api/v1/dev') || 'http://localhost:3001/api/v1/dev';

// Axios client that represents Sentia login in dev
const sentiaApi = axios.create({
  baseURL: SENTIA_BASE_URL,
});

// Axios client for the real Tasks API endpoints
const tasksApi = axios.create({
  baseURL: TASKS_BASE_URL,
});

// Attach Authorization: Bearer <token> to every Tasks API request
tasksApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Warn on 401 or network issues
tasksApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      console.warn(
        '[tasksApi] 401 Unauthorized from Tasks API. Token may be missing or expired.'
      );
    } else if (!error.response) {
      console.warn('[tasksApi] Network/connection error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ---------- Service wrappers ----------

export const sentiaAuthService = {
  // Development login (fake Sentia login)
  login: (email, password) =>
    sentiaApi.post('/auth/login', { email, password }),
};

export const tasksService = {
  // Identity sync
  getMe: () => tasksApi.get('/auth/me'),

  // Workspaces
  createWorkspace: (name) => tasksApi.post('/workspaces', { name }),
  getMyWorkspaces: () => tasksApi.get('/workspaces'),
  getWorkspaceLists: (workspaceId) =>
    tasksApi.get(`/workspaces/${encodeURIComponent(workspaceId)}/lists`),
  updateWorkspace: (workspaceId, name) =>
    tasksApi.patch(`/workspaces/${encodeURIComponent(workspaceId)}`, { name }),
  deleteWorkspace: (workspaceId) =>
    tasksApi.delete(`/workspaces/${encodeURIComponent(workspaceId)}`),

  // Lists
  createList: (workspace_id, title) =>
    tasksApi.post('/lists', { workspace_id, title }),
  getListDetails: (listId) =>
    tasksApi.get(`/lists/${encodeURIComponent(listId)}`),
  updateList: (listId, title) =>
    tasksApi.patch(`/lists/${encodeURIComponent(listId)}`, { title }),
  deleteList: (listId) =>
    tasksApi.delete(`/lists/${encodeURIComponent(listId)}`),

  // Tasks
  getTasks: (listId) =>
    tasksApi.get(`/tasks/${encodeURIComponent(listId)}`),
  createTask: (listId, title, description) =>
    tasksApi.post(`/tasks/${encodeURIComponent(listId)}`, {
      title,
      description,
    }),
  updateTask: (taskId, updates) =>
    tasksApi.patch(`/tasks/${encodeURIComponent(taskId)}`, updates),
  updateTaskPosition: (taskId, position, status) =>
    tasksApi.patch(`/tasks/${encodeURIComponent(taskId)}/position`, {
      position,
      status,
    }),
  searchTasks: (workspaceId, filters) =>
    tasksApi.get(`/tasks/search/${encodeURIComponent(workspaceId)}`, {
      params: filters, // { q, status, assignee, labels, due_from, due_to }
    }),
  copyTask: (taskId, targetListId, options) =>
    tasksApi.post(`/tasks/${encodeURIComponent(taskId)}/copy`, {
      targetListId,
      options
    }),

  // Invites / collaborators
  createInvites: (listId, emails, permission_level) =>
    tasksApi.post(`/invites/${encodeURIComponent(listId)}`, {
      emails,
      permission_level,
    }),
  acceptInvite: (token) =>
    tasksApi.post('/invites/accept', { token }),
  getListCollaborators: (listId) =>
    tasksApi.get(
      `/invites/${encodeURIComponent(listId)}/collaborators`
    ),
  revokeInvite: (inviteId) =>
    tasksApi.delete(`/invites/${encodeURIComponent(inviteId)}`),
  removeCollaborator: (listId, userId) =>
    tasksApi.delete(
      `/invites/${encodeURIComponent(listId)}/collaborators/${encodeURIComponent(userId)}`
    ),

  // Templates
  getTemplates: () => tasksApi.get('/templates'),

  // Labels
  getLabelsForList: (listId) =>
    tasksApi.get(`/labels/${encodeURIComponent(listId)}`),
  createLabel: (listId, name, color) =>
    tasksApi.post(`/labels/${encodeURIComponent(listId)}`, { name, color }),
  updateLabel: (labelId, updates) =>
    tasksApi.patch(`/labels/${encodeURIComponent(labelId)}`, updates),
  deleteLabel: (labelId) =>
    tasksApi.delete(`/labels/${encodeURIComponent(labelId)}`),
  getLabelsForTask: (taskId) =>
    tasksApi.get(`/labels/task/${encodeURIComponent(taskId)}`),
  addLabelToTask: (taskId, labelId) =>
    tasksApi.post(`/labels/task/${encodeURIComponent(taskId)}/add`, { labelId }),
  removeLabelFromTask: (taskId, labelId) =>
    tasksApi.delete(`/labels/task/${encodeURIComponent(taskId)}/remove/${encodeURIComponent(labelId)}`),

  // Comments
  getCommentsForTask: (taskId) =>
    tasksApi.get(`/comments/${encodeURIComponent(taskId)}`),
  createComment: (taskId, content) =>
    tasksApi.post(`/comments/${encodeURIComponent(taskId)}`, { content }),
  updateComment: (commentId, content) =>
    tasksApi.patch(`/comments/${encodeURIComponent(commentId)}`, { content }),
  deleteComment: (commentId) =>
    tasksApi.delete(`/comments/${encodeURIComponent(commentId)}`),
  markCommentsAsRead: (taskId) =>
    tasksApi.post(`/comments/${encodeURIComponent(taskId)}/mark-read`),

  // Activity Log
  getActivityForTask: (taskId) =>
    tasksApi.get(`/activity/${encodeURIComponent(taskId)}`),

  // Attachments
  uploadAttachment: (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return tasksApi.post(`/attachments/${encodeURIComponent(taskId)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getTaskAttachments: (taskId) =>
    tasksApi.get(`/attachments/task/${encodeURIComponent(taskId)}`),
  downloadAttachment: (attachmentId) =>
    tasksApi.get(`/attachments/${encodeURIComponent(attachmentId)}/download`, {
      responseType: 'blob', // Important for file downloads
    }),
  deleteAttachment: (attachmentId) =>
    tasksApi.delete(`/attachments/${encodeURIComponent(attachmentId)}`),
};

export { sentiaApi, tasksApi };
